/**
 * SolutionMapPage — IVT Hub 战略层全景视图。
 *
 * Step 1：Topic 视图（查看 + 编辑，走真实 Topic ops）。
 * Step 2：Industry 视图 editMode（cell / area / span 编辑），走 UpdateSolutionMap /
 *   ReorderSolutionMap / RenameSolutionMapUc / RenameSolutionMapArea /
 *   DeprecateSolutionMapArea，全部经由 useSolutionMapMutations 统一处理 lock + reload。
 *
 * 整列改名和整列删除各是**一个原子 op**，不是多步事务 —— 它们曾经是前端循环，会重建 UC
 * 行（换掉 solution_map.id，孤立 uc_asset_link）、且可能半途失败留下中间态。现在那个中间
 * 态不可能出现，所以任何"已写入 N/M 步"式的告警都是在描述一个不存在的状态。
 *
 * ⭐ **所有针对已存在行的写操作都按 `solutionMapId` 寻址**，因为 UC 名不再是稳定标识：删掉再
 * 同名重建会得到新的 row id，所以拿早先读到的名字去写可能命中用户没看见的另一行、而后端返回
 * 200。覆盖：renameUc · reorder(MOVE_UC) · 单 UC 软删 · priority 循环 · 跨列 UC 覆盖范围。
 * hook 把这些路径的 id 做成**必填参数**，所以「按名字编辑」在本页不可达（漏传是编译错）。
 *
 * **两类写操作不带 id**：① 新建（建 UC / 建列 / 建跨列 UC）—— 行还不存在，业务键是唯一的寻址
 * 方式，这也是 model 上 `solutionMapId` 是 optional 的真正原因（不是为了向后兼容）；② **整列级**
 * 操作（改列名 / 删整列 / 左右移列）—— area 不是实体，没有表也没有 id，只有它的行共享的那个名字，
 * 所以它们按 (industry, area) 寻址，且仍然带着陈旧名字的风险（列被别人改名后请求会 404 —— 这是
 * 刻意的，见各自的 404 分支）。
 * ⚠️ 但 `createEntry` 是 upsert：用一个已存在的活行名字去"新建"会命中那一行走 UPDATE。所以
 * 「按名字寻址不可达」这句**只对编辑路径成立**，不要推广到 createEntry。
 *
 * 数据流：
 *   - 初始：GetSolutionMapAll 一次拿回全部行业 → setMaps
 *   - 写操作后：useSolutionMapMutations.reloadIndustry(industry) 单行业回流
 *     （cycle priority / rename UC 走局部 patchEntry，无需 reload）
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Spinner } from '@amzn/awsui-components-react';
import PageHeader from '../../../components/PageHeader/PageHeader';
import {
  getIvtClient,
  GetSolutionMapAllCommand,
  GetSolutionMapCommand,
  ReorderDirection,
  type IndustryMeta,
  type GetSolutionMapCommandOutput,
  type SolutionMapEntry,
} from '../../client/IvtHubClient';
import { sortIndustries } from '../../utils/sort';
import ConfirmDialog from './components/ConfirmDialog';
import IndustryMatrix from './components/IndustryMatrix';
import NewAreaModal from './components/NewAreaModal';
import NewSpanUcModal from './components/NewSpanUcModal';
import NewUcModal from './components/NewUcModal';
import RenameAreaModal from './components/RenameAreaModal';
import SolutionMapToolbar, { type SolutionMapView } from './components/SolutionMapToolbar';
import TopicGrid from './components/TopicGrid';
import { useSolutionMapMutations } from './hooks/useSolutionMapMutations';
import { nextPriority } from './utils/priorityCycle';
import { serializeSpanCoverage, SPAN_AREA } from './utils/spanCoverage';
import './SolutionMapPage.css';

const COLUMN_HEADER_BG = '#252525';

interface RenameWarningState {
  industry: string;
  entry: SolutionMapEntry;
  newName: string;
}

interface DeleteUcConfirmState {
  industry: string;
  entry: SolutionMapEntry;
}

interface DeleteAreaConfirmState {
  industry: string;
  area: string;
  /** LIVE UC count, shown in the confirm text. `DeprecateSolutionMapArea` finds the rows
   *  itself, so the entries themselves are no longer needed here. */
  ucCount: number;
}

interface RenameAreaModalState {
  industry: string;
  area: string;
  /** LIVE UC count, shown in the modal. `RenameSolutionMapArea` finds the rows itself,
   *  so the entries themselves are no longer needed here. */
  ucCount: number;
}

interface NewUcModalState {
  industry: string;
  area: string;
}

interface NewSpanUcModalState {
  industry: string;
  areaNames: string[];
}

export default function SolutionMapPage() {
  const [industries, setIndustries] = useState<IndustryMeta[] | null>(null);
  const [maps, setMaps] = useState<Map<string, GetSolutionMapCommandOutput>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const [view, setView] = useState<SolutionMapView>('industry');
  const [editMode, setEditMode] = useState(false);
  const [editingIndustry, setEditingIndustry] = useState<string | null>(null);
  const [selectedIndustry, setSelectedIndustry] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [addTopicTrigger, setAddTopicTrigger] = useState(0);

  // ── Modal / confirm state ────────────────────────────────────────────────
  const [renameWarning, setRenameWarning] = useState<RenameWarningState | null>(null);
  const [deleteUcConfirm, setDeleteUcConfirm] = useState<DeleteUcConfirmState | null>(null);
  const [deleteAreaConfirm, setDeleteAreaConfirm] = useState<DeleteAreaConfirmState | null>(null);
  const [renameAreaModal, setRenameAreaModal] = useState<RenameAreaModalState | null>(null);
  const [newUcModal, setNewUcModal] = useState<NewUcModalState | null>(null);
  const [newAreaModal, setNewAreaModal] = useState<{ industry: string } | null>(null);
  const [newSpanUcModal, setNewSpanUcModal] = useState<NewSpanUcModalState | null>(null);
  /**
   * Failure message for whichever ConfirmDialog is open. The three confirms used to be
   * fire-and-forget — `void muts.x()` then close immediately — so a 403 or 404 only reached
   * the page-level Alert at the very TOP of the page. With several industry matrices stacked,
   * that Alert is off-screen, and the user saw the dialog close with the row still there and
   * concluded the write had been accepted. The three Modals on this page already awaited
   * their result and stayed open on failure; these now match. One slot suffices because the
   * dialogs are mutually exclusive.
   */
  const [confirmError, setConfirmError] = useState<string | null>(null);

  // ── Initial load (single call returns all industries + their entries) ────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const client = getIvtClient();
        const res = await client.send(new GetSolutionMapAllCommand({}));
        if (cancelled) return;
        const all = res.industries ?? [];

        // Build IndustryMeta list (downstream components expect assetCount etc.)
        const metaList: IndustryMeta[] = all.map((ind) => ({
          industry: ind.industry,
          industryType: ind.industryType,
          solutionMapUrl: ind.solutionMapUrl,
          assetCount: 0,
          activeMotionCount: 0,
        }));
        setIndustries(metaList);

        // Populate per-industry maps (reloadIndustry / patchEntry still update these)
        const m = new Map<string, GetSolutionMapCommandOutput>();
        for (const ind of all) {
          if (ind.industry) {
            m.set(ind.industry, {
              industry: ind.industry,
              industryType: ind.industryType,
              solutionMapUrl: ind.solutionMapUrl,
              entries: ind.entries,
              $metadata: {},
            });
          }
        }
        setMaps(m);
      } catch (e) {
        if (cancelled) return;
        setError((e as Error).message ?? 'Unknown error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  // ── Per-industry refetch (used after Reorder / Add / Rename area / Delete area) ──
  // Plain SDK. This used to go through a hand-rolled SigV4 `fetch` because the generated
  // `SolutionMapEntry` did not declare `notes`, and the schema-based deserializer drops
  // undeclared members — which would have silently emptied every cross-area UC's coverage
  // list. `notes` is declared now, so the bypass (and its bespoke signing path) is gone.
  const reloadIndustry = useCallback(async (industry: string) => {
    const out = await getIvtClient().send(new GetSolutionMapCommand({ industry }));
    setMaps((prev) => {
      const next = new Map(prev);
      next.set(industry, out);
      return next;
    });
  }, []);

  // ── Patch a single entry by id (used after cyclePriority / rename UC) ───
  const patchEntry = useCallback((industry: string, entry: SolutionMapEntry) => {
    setMaps((prev) => {
      const old = prev.get(industry);
      if (!old) return prev;
      const nextEntries = (old.entries ?? []).map((e) => (e.id === entry.id ? entry : e));
      const next = new Map(prev);
      next.set(industry, { ...old, entries: nextEntries });
      return next;
    });
  }, []);

  const muts = useSolutionMapMutations({ reloadIndustry, patchEntry });

  // ── Cell-level handlers ─────────────────────────────────────────────────

  const handleCyclePriority = useCallback(
    (industry: string, entry: SolutionMapEntry) => {
      if (entry.useCase == null || entry.solutionArea == null || entry.id == null) return;
      const newPriority = nextPriority(entry.priority);
      // Optimistic patch: write the new priority into the local entry now.
      const optimistic = { ...entry, priority: newPriority };
      patchEntry(industry, optimistic);
      void muts.updateEntry(
        {
          industry,
          solutionArea: entry.solutionArea,
          useCase: entry.useCase,
          priority: newPriority,
          solutionMapId: entry.id,
        },
        // rollback: restore original entry on failure
        () => patchEntry(industry, entry),
      );
    },
    [muts, patchEntry],
  );

  const handleMoveUc = useCallback(
    (industry: string, entry: SolutionMapEntry, direction: 'UP' | 'DOWN') => {
      if (entry.useCase == null || entry.solutionArea == null || entry.id == null) return;
      void muts.reorderUc({
        industry,
        solutionMapId: entry.id,
        solutionArea: entry.solutionArea,
        useCase: entry.useCase,
        direction: ReorderDirection[direction],
      });
    },
    [muts],
  );

  const handleStartRenameUc = useCallback(
    (industry: string, entry: SolutionMapEntry, newName: string) => {
      // Show the prototype's confirm warning before issuing the rename.
      setConfirmError(null);
      setRenameWarning({ industry, entry, newName });
    },
    [],
  );

  const handleConfirmRenameUc = useCallback(async () => {
    if (!renameWarning) return;
    const { industry, entry, newName } = renameWarning;
    if (!entry.useCase || entry.solutionArea == null || entry.id == null) {
      setRenameWarning(null);
      return;
    }
    const oldName = entry.useCase;
    const optimistic = { ...entry, useCase: newName };
    patchEntry(industry, optimistic);
    setConfirmError(null);
    const failure = await muts.renameUc({
      industry,
      solutionArea: entry.solutionArea,
      oldName,
      newName,
      // Addresses the row, so a name this tab read before someone else deleted and
      // re-created the UC 404s instead of renaming a row the user never saw.
      solutionMapId: entry.id,
      rollback: () => patchEntry(industry, entry),
    });
    setConfirmError(failure);
    if (!failure) setRenameWarning(null);
  }, [muts, patchEntry, renameWarning]);

  const handleSoftDeleteUc = useCallback(
    (industry: string, entry: SolutionMapEntry) => {
      setConfirmError(null);
      setDeleteUcConfirm({ industry, entry });
    },
    [],
  );

  const handleConfirmDeleteUc = useCallback(async () => {
    if (!deleteUcConfirm) return;
    const { industry, entry } = deleteUcConfirm;
    if (entry.useCase == null || entry.solutionArea == null || entry.id == null) {
      setDeleteUcConfirm(null);
      return;
    }
    // By id, so a name this tab read before someone else deleted and re-created the UC
    // cannot delete the replacement. Deleting the same id twice is a 200 (@idempotent), so
    // a double-click is safe for correctness.
    setConfirmError(null);
    const failure = await muts.updateEntry({
      industry,
      solutionArea: entry.solutionArea,
      useCase: entry.useCase,
      status: 'DEPRECATED',
      solutionMapId: entry.id,
    });
    setConfirmError(failure);
    if (!failure) setDeleteUcConfirm(null);
  }, [deleteUcConfirm, muts]);

  // ── Area-level handlers ─────────────────────────────────────────────────

  const handleAddUc = useCallback((industry: string, area: string) => {
    setNewUcModal({ industry, area });
  }, []);

  const handleSaveNewUc = useCallback(
    async (input: { useCase: string; priority: number }) => {
      // No modal state = nothing to save. Not a failure, so no message.
      if (!newUcModal) return null;
      const failure = await muts.createEntry({
        industry: newUcModal.industry,
        solutionArea: newUcModal.area,
        useCase: input.useCase,
        priority: input.priority,
        status: 'ACTIVE',
      }, 'uc');
      if (!failure) setNewUcModal(null);
      return failure;
    },
    [muts, newUcModal],
  );

  const handleMoveArea = useCallback(
    (industry: string, area: string, direction: 'LEFT' | 'RIGHT') => {
      void muts.reorderArea({ industry, area, direction: ReorderDirection[direction] });
    },
    [muts],
  );

  // `ucCount` comes from IndustryMatrix, which groups from its `active` set (status !==
  // DEPRECATED); this used to recompute it from `maps` instead. Both halves of that fix
  // matter and only one is still load-bearing: the read stopped returning tombstones in
  // CR-302136400, so `maps` should now hold live rows only — but taking the count from the
  // set that was actually RENDERED is what makes the dialog agree with the matrix by
  // construction, rather than by two filters happening to match.
  const handleOpenRenameArea = useCallback((industry: string, area: string, ucCount: number) => {
    setRenameAreaModal({ industry, area, ucCount });
  }, []);

  const handleSaveRenameArea = useCallback(
    async (input: { newArea: string }) => {
      // No modal state = nothing to save. Not a failure, so no message.
      if (!renameAreaModal) return null;
      const failure = await muts.renameArea({
        industry: renameAreaModal.industry,
        oldArea: renameAreaModal.area,
        newArea: input.newArea,
      });
      if (!failure) setRenameAreaModal(null);
      return failure;
    },
    [muts, renameAreaModal],
  );

  // Same as rename: the count comes from the set IndustryMatrix rendered, not from a second
  // filter over `maps`. The names are no longer collected at all — `DeprecateSolutionMapArea`
  // finds the column's live rows server-side, so nothing here can hand it a stale one.
  const handleOpenDeleteArea = useCallback((industry: string, area: string, ucCount: number) => {
    setConfirmError(null);
    setDeleteAreaConfirm({ industry, area, ucCount });
  }, []);

  const handleConfirmDeleteArea = useCallback(async () => {
    if (!deleteAreaConfirm) return;
    const { industry, area } = deleteAreaConfirm;
    setConfirmError(null);
    const failure = await muts.deprecateArea({ industry, area });
    setConfirmError(failure);
    if (!failure) setDeleteAreaConfirm(null);
  }, [deleteAreaConfirm, muts]);

  // ── Industry-level handlers ─────────────────────────────────────────────

  const handleOpenAddArea = useCallback((industry: string) => {
    setNewAreaModal({ industry });
  }, []);

  const handleSaveNewArea = useCallback(
    async (input: { areaName: string; placeholderUcName: string }) => {
      // No modal state = nothing to save. Not a failure, so no message.
      if (!newAreaModal) return null;
      // 'column': the write is indistinguishable from adding a UC, but a 409 here is about
      // the PLACEHOLDER UC name, not the column name — and NewAreaModal pre-fills that
      // placeholder, so accepting the default twice is the likeliest way to trip it.
      const failure = await muts.createEntry({
        industry: newAreaModal.industry,
        solutionArea: input.areaName,
        useCase: input.placeholderUcName,
        priority: 0,
        status: 'ACTIVE',
      }, 'column');
      if (!failure) setNewAreaModal(null);
      return failure;
    },
    [muts, newAreaModal],
  );

  // ── Span row handlers ───────────────────────────────────────────────────

  const handleOpenAddSpanUc = useCallback(
    (industry: string) => {
      const realAreaNames = (maps.get(industry)?.entries ?? [])
        .filter((e) => e.solutionArea && e.solutionArea !== SPAN_AREA)
        .map((e) => e.solutionArea as string);
      const uniqueAreas = Array.from(new Set(realAreaNames));
      setNewSpanUcModal({ industry, areaNames: uniqueAreas });
    },
    [maps],
  );

  const handleSaveNewSpanUc = useCallback(
    async (input: { useCase: string; priority: number; coveredAreas: string[] }) => {
      // No modal state = nothing to save. Not a failure, so no message.
      if (!newSpanUcModal) return null;
      const failure = await muts.createEntry({
        industry: newSpanUcModal.industry,
        solutionArea: SPAN_AREA,
        useCase: input.useCase,
        priority: input.priority,
        status: 'ACTIVE',
        notes: serializeSpanCoverage(input.coveredAreas),
      }, 'span');
      if (!failure) setNewSpanUcModal(null);
      return failure;
    },
    [muts, newSpanUcModal],
  );

  const handleUpdateSpanCoverage = useCallback(
    (industry: string, entry: SolutionMapEntry, coveredAreas: string[]) => {
      if (!entry.useCase || entry.id == null) return;
      // Editing an existing span row, not creating one — it used to go through createEntry
      // and be addressed by (industry, '_span_', useCase), i.e. by name. SpanRow's
      // drag-resize is now the only way to change coverage; the checkbox modal that also did
      // it was unreachable (SpanRow never called its prop) and has been deleted.
      void muts.updateSpanCoverage({
        industry,
        solutionMapId: entry.id,
        useCase: entry.useCase,
        notes: serializeSpanCoverage(coveredAreas),
      });
    },
    [muts],
  );

  // ── Derived state ───────────────────────────────────────────────────────

  const allIndustries = useMemo(
    () => sortIndustries(industries ?? []),
    [industries],
  );

  const filteredIndustries = useMemo(() => {
    if (selectedIndustry === 'ALL') return allIndustries;
    return allIndustries.filter((i) => i.industry === selectedIndustry);
  }, [allIndustries, selectedIndustry]);

  const industryOptions = useMemo(
    () => allIndustries.map((i) => i.industry).filter((n): n is string => !!n),
    [allIndustries],
  );

  const handleToggleIndustryEdit = useCallback((industry: string) => {
    setEditingIndustry((prev) => {
      const next = prev === industry ? null : industry;
      setEditMode(next !== null);
      if (next !== null) setSearchQuery('');
      return next;
    });
  }, []);

  const matrixHandlers = {
    onCyclePriority: handleCyclePriority,
    onMoveUc: handleMoveUc,
    onRenameUc: handleStartRenameUc,
    onSoftDeleteUc: handleSoftDeleteUc,
    onAddUc: handleAddUc,
    onMoveArea: handleMoveArea,
    onRenameArea: handleOpenRenameArea,
    onDeleteArea: handleOpenDeleteArea,
    onAddArea: handleOpenAddArea,
    onAddSpanUc: handleOpenAddSpanUc,
    onUpdateSpanCoverage: handleUpdateSpanCoverage,
  };

  return (
    <div className="ivt-solution-map-page">
      <div className="ivt-solution-map-page__header">
        <PageHeader title={view === 'topic' ? 'Topic' : 'Solution Map'} />
      </div>

      <SolutionMapToolbar
        view={view}
        editMode={editMode}
        onToggleView={() => setView((v) => (v === 'industry' ? 'topic' : 'industry'))}
        onToggleEdit={() => setEditMode((v) => !v)}
        onAddTopic={() => setAddTopicTrigger((n) => n + 1)}
        selectedIndustry={selectedIndustry}
        onIndustryChange={setSelectedIndustry}
        industryOptions={industryOptions}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
      />

      {view === 'industry' && (
        <>
          {loading && (
            <div className="ivt-solution-map-page__loading">
              <Spinner size="large" />
              <span>Loading Solution Map…</span>
            </div>
          )}

          {!loading && error && (
            <Alert
              type="error"
              header="Failed to load Solution Map"
              action={<button onClick={() => setReloadKey((k) => k + 1)}>Retry</button>}
            >
              {error}
            </Alert>
          )}

          {editMode && muts.error && (
            <Alert
              type="error"
              dismissible
              onDismiss={() => muts.dismissError()}
            >
              {muts.error}
            </Alert>
          )}

          {!loading && !error && industries && industries.length === 0 && (
            <div className="ivt-solution-map-page__empty">No Solution Map configured.</div>
          )}

          {!loading && !error && filteredIndustries.length > 0 && (
            <>
              {filteredIndustries.map((i) => (
                <IndustryMatrix
                  key={i.industry}
                  meta={i}
                  data={maps.get(i.industry ?? '')}
                  columnHeaderColor={COLUMN_HEADER_BG}
                  editMode={editingIndustry === i.industry}
                  locked={muts.isLocked(i.industry ?? '')}
                  onToggleEdit={handleToggleIndustryEdit}
                  searchQuery={searchQuery}
                  {...matrixHandlers}
                />
              ))}
            </>
          )}
        </>
      )}

      {view === 'topic' && (
        <TopicGrid
          active
          editMode={editMode}
          addTopicTrigger={addTopicTrigger}
        />
      )}

      {/* ── Modals & confirms ───────────────────────────────────────────── */}

      {renameWarning && (
        <ConfirmDialog
          title="重命名 UC"
          message={
            <>
              将「<strong>{renameWarning.entry.useCase}</strong>」改名为「
              <strong>{renameWarning.newName}</strong>」？
            </>
          }
          confirmLabel="重命名"
          busy={muts.isLocked(renameWarning.industry)}
          onCancel={() => setRenameWarning(null)}
          error={confirmError}
          onConfirm={handleConfirmRenameUc}
        />
      )}

      {deleteUcConfirm && (
        <ConfirmDialog
          title="Delete Use Case"
          message={
            <>
              将 UC「<strong>{deleteUcConfirm.entry.useCase}</strong>」标记为
              <span style={{ color: '#d91515', fontWeight: 600 }}> DEPRECATED</span>？
              <br />
              <br />
              该 UC 会从矩阵、Use Cases 和 Assets 树中消失（不再显示），但数据库里保留，
              其弹药库和 Tag 数据不丢失，可由后端恢复。
            </>
          }
          confirmLabel="Delete"
          cancelLabel="Cancel"
          busy={muts.isLocked(deleteUcConfirm.industry)}
          onCancel={() => setDeleteUcConfirm(null)}
          error={confirmError}
          onConfirm={handleConfirmDeleteUc}
        />
      )}

      {deleteAreaConfirm && (
        <ConfirmDialog
          title="软删整列 Solution Area"
          message={
            <>
              将整列「<strong>{deleteAreaConfirm.area}</strong>」标记为
              DEPRECATED？
              <br />
              <br />
              会软删该 Area 下所有 UC（共 <strong>{deleteAreaConfirm.ucCount}</strong> 条）。
              这一列和它的 UC 会从矩阵、Use Cases 和 Assets 树中消失（不再显示），但数据库里
              保留，可由后端恢复。跨列 UC 的覆盖范围会同步去掉这一列。
            </>
          }
          confirmLabel="删除"
          danger
          busy={muts.isLocked(deleteAreaConfirm.industry)}
          onCancel={() => setDeleteAreaConfirm(null)}
          error={confirmError}
          onConfirm={handleConfirmDeleteArea}
        />
      )}

      {renameAreaModal && (
        <RenameAreaModal
          industry={renameAreaModal.industry}
          oldArea={renameAreaModal.area}
          ucCount={renameAreaModal.ucCount}
          saving={muts.isLocked(renameAreaModal.industry)}
          onClose={() => setRenameAreaModal(null)}
          onSave={handleSaveRenameArea}
        />
      )}

      {newUcModal && (
        <NewUcModal
          industry={newUcModal.industry}
          solutionArea={newUcModal.area}
          saving={muts.isLocked(newUcModal.industry)}
          onClose={() => setNewUcModal(null)}
          onSave={handleSaveNewUc}
        />
      )}

      {newAreaModal && (
        <NewAreaModal
          industry={newAreaModal.industry}
          saving={muts.isLocked(newAreaModal.industry)}
          onClose={() => setNewAreaModal(null)}
          onSave={handleSaveNewArea}
        />
      )}

      {newSpanUcModal && (
        <NewSpanUcModal
          industry={newSpanUcModal.industry}
          areaNames={newSpanUcModal.areaNames}
          saving={muts.isLocked(newSpanUcModal.industry)}
          onClose={() => setNewSpanUcModal(null)}
          onSave={handleSaveNewSpanUc}
        />
      )}
    </div>
  );
}
